import * as t from 'io-ts';

import {RCompoundConditions} from '../../settings/editors/conditions/compoundConditionModel';
import {
  RPreExpression,
  RStringOrTemplate,
  RTeammateIdOfRef,
} from '../../settings/editors/expressions/iotsCodecs';
import {makeApiType, makeEnumRuntimeType} from './apiTypes';

/*
 * Runtime types.
 */

const RPreviewBuilder = t.readonly(
  t.type({
    name: RStringOrTemplate,
    description: t.union([RStringOrTemplate, t.undefined]),
    canonicalUrl: t.union([RStringOrTemplate, t.undefined]),
    status: t.union([RStringOrTemplate, t.undefined]),
    ownerId: t.union([RTeammateIdOfRef, t.undefined]),
  }),
);

export enum PreviewerLinkTypesEnum {
  BOOKING = 'booking',
  DOCUMENT = 'document',
  FLIGHT = 'flight',
  ISSUE = 'issue',
  MORTGAGE = 'mortgage',
  OPPORTUNITY = 'opportunity',
  ORDER = 'order',
  PERSON = 'person',
  SHIP = 'ship',
  SHIPMENT = 'shipment',
  STAY = 'stay',
  TASK = 'task',
}
export const RPreviewerLinkTypesEnum = makeEnumRuntimeType(PreviewerLinkTypesEnum);

const RPreviewer = t.readonly(
  t.type({
    id: t.Integer,
    url: t.string,
    linkType: RPreviewerLinkTypesEnum,
    domain: t.string,
    description: t.union([t.undefined, t.string]),
    exampleUrl: t.union([t.undefined, t.string]),
    expressions: t.readonlyArray(RPreExpression),
    compoundConditions: RCompoundConditions,
    previewBuilder: RPreviewBuilder,
    createdAt: t.Integer,
  }),
);

const RPreviewersSearch = t.readonly(
  t.type({
    items: t.readonlyArray(RPreviewer),
    total: t.number,
    limit: t.number,
  }),
);

/*
 * Types.
 */

export interface PreviewBuilderModel extends t.TypeOf<typeof RPreviewBuilder> {}

export interface PreviewerModel extends t.TypeOf<typeof RPreviewer> {}

export interface UnsavedPreviewerModel extends Omit<PreviewerModel, 'id' | 'url' | 'createdAt'> {}

export interface PreviewersSearchModel extends t.TypeOf<typeof RPreviewersSearch> {}

/*
 * IO methods.
 */

export function parsePreviewer(src: any): PreviewerModel {
  return {
    id: src.id,
    url: src.url,
    linkType: src.link_type,
    domain: src.domain,
    description: src.description ?? undefined,
    exampleUrl: src.example_url ?? undefined,
    expressions: src.expressions,
    compoundConditions: src.compound_conditions ?? [],
    previewBuilder: src.preview_builder,
    createdAt: src.created_at,
  };
}

function parsePreviewersSearch(src: any): PreviewersSearchModel {
  return {
    items: src.items.map(parsePreviewer),
    total: src.total,
    limit: src.limit,
  };
}

export function serializePreviewer(src: UnsavedPreviewerModel): any {
  return {
    link_type: src.linkType,
    domain: src.domain,
    description: src.description ?? null,
    example_url: src.exampleUrl ?? null,
    expressions: src.expressions,
    compound_conditions: src.compoundConditions,
    preview_builder: src.previewBuilder,
  };
}

/*
 * API types.
 */

export const ApiPreviewer = makeApiType('previewer', RPreviewer, parsePreviewer);
export const ApiPreviewersSearch = makeApiType('previewersSearch', RPreviewersSearch, parsePreviewersSearch);
